import React, { useEffect, useState } from "react";
import { useCart } from "./CartContext";

export default function Cart() {
  const { cart, removeFromCart, clearCart } = useCart();

  const cartItems = Object.entries(cart).map(([id, qty]) => {
    const product = JSON.parse(localStorage.getItem(`product_${id}`));
    return { ...product, quantity: qty };
  });

  const handleCheckout = async () => {
    const line_items = cartItems.map((item) => ({
      price_data: {
        currency: "aud",
        product_data: { name: item.name },
        unit_amount: item.price,
      },
      quantity: item.quantity,
    }));

    const res = await fetch("https://stripe-shop-backend-gsck.onrender.com/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line_items }),
    });
    const data = await res.json();
    window.location = data.url;
  };

  return (
    <div className="cart-cyberpunk">
      <h3>Your Cart</h3>
      {cartItems.length === 0 ? (
        <p className="cart-empty">Your cart is empty.</p>
      ) : (
        <ul className="cart-list">
          {cartItems.map((item) => (
            <li key={item.id} className="cart-item">
              <span className="cart-item-name">{item.name}</span>
              <span className="cart-item-qty">x {item.quantity}</span>
              <span className="cart-item-total">${(item.price / 100 * item.quantity).toFixed(2)}</span>
              <button onClick={() => removeFromCart(item.id)} style={{marginLeft: 8}}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="cart-total-row">
        <span className="cart-total-label">Total:</span>
        <span className="cart-total-value">${(cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0) / 100).toFixed(2)}</span>
      </div>
      <button className="cart-checkout-btn" onClick={handleCheckout} disabled={cartItems.length === 0} style={{display: 'none'}}>
        Checkout
      </button>
      <button onClick={clearCart} disabled={cartItems.length === 0} style={{marginTop: 8}}>Clear Cart</button>
    </div>
  );
}

export function ProductList() {
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalImageIdx, setModalImageIdx] = useState(0);
  const [category, setCategory] = useState('all-items');
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const PRODUCTS_PER_PAGE = 15;

  // Read category and search from URL hash on mount and when hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '').toLowerCase();
      if (hash === 'search') {
        setSearchVisible(true);
        setCategory('all-items');
      } else {
        setSearchVisible(false);
        setCategory(hash || 'all-items');
      }
      setPage(1);
    };
    handleHashChange(); // Initial
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    fetch("https://stripe-shop-backend-gsck.onrender.com/products")
      .then((res) => res.json())
      .then((data) => setProducts(data));
  }, []);

  // Always show these categories, plus any found in products
  const defaultCategories = ['all-items', 'tops', 'bottoms', 'dresses', 'accessories'];
  const categories = Array.from(new Set([
    ...defaultCategories,
    ...products.map(p => (p.category || '').toLowerCase().trim() ? p.category.toLowerCase().trim() : 'all-items')
  ]));

  // Filter products by search query and category
  const filteredProducts = products.filter(p => {
    const prodCat = (p.category || '').toLowerCase().trim();
    const matchesCategory = category === 'all-items' || prodCat === category;
    const matchesSearch = !searchVisible || searchQuery.trim() === '' ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return (searchVisible ? matchesSearch : matchesCategory && matchesSearch);
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice((page - 1) * PRODUCTS_PER_PAGE, page * PRODUCTS_PER_PAGE);

  // Reset modal image index when opening a new product
  useEffect(() => { setModalImageIdx(0); }, [selectedProduct]);

  // Helper to check for debug query param
  function isDebug() {
    return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';
  }

  return (
    <div>
      {isDebug() && (
        <div style={{background: '#fffbe6', color: '#222', padding: 8, border: '1px solid #ffe066', borderRadius: 8, margin: '8px auto', maxWidth: 420, fontSize: 14}}>
          <strong>DEBUG:</strong> hash: <code>{window.location.hash}</code>, category: <code>{category}</code>, search: <code>{searchQuery}</code>, products: <code>{filteredProducts.length}</code>
        </div>
      )}
      {searchVisible && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '18px 0 8px 0' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            style={{
              padding: '10px 18px',
              borderRadius: '999px',
              border: '1.5px solid #ffe066',
              fontSize: '1rem',
              fontFamily: 'Space Grotesk, Orbitron, Segoe UI, Roboto, Helvetica Neue, Ubuntu, sans-serif',
              width: '100%',
              maxWidth: 420,
              outline: 'none',
              background: '#fff',
              color: '#18182f',
              boxShadow: '0 2px 12px 0 #3a3afc22',
              marginBottom: 8
            }}
            autoFocus
          />
        </div>
      )}
      {/* Category label above products */}
      <h2 style={{textAlign: 'center', margin: '18px 0 8px 0'}}>
        {searchVisible ? 'All Items' : (category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
      </h2>
      <div className="product-grid">
        {paginatedProducts.map((product) => {
          const price = product.prices[0];
          // Store product info for cart display
          localStorage.setItem(`product_${product.id}`, JSON.stringify({
            id: product.id,
            name: product.name,
            price: price ? price.unit_amount : 0,
            priceId: price ? price.id : null,
          }));
          return (
            <div key={product.id} className="product-card" onClick={() => setSelectedProduct(product)} style={{cursor: 'pointer'}}>
              <img
                src={product.images && product.images.length > 0 ? product.images[0] : 'https://via.placeholder.com/300x300?text=No+Image'}
                alt={product.name}
                className="product-image"
              />
              <div className="product-info">
                <h3 className="product-title">{product.name}</h3>
                <p className="product-description">{product.description}</p>
                <div className="product-bottom-row">
                  <span className="product-price">{price ? `$${(price.unit_amount / 100).toFixed(2)}` : 'N/A'}</span>
                  <button onClick={e => { e.stopPropagation(); addToCart(product.id, 1); }} disabled={!price} className="add-to-cart-btn">Add to Cart</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button onClick={() => setPage(page - 1)} disabled={page === 1}>Previous</button>
        <span>Page {page} of {totalPages}</span>
        <button onClick={() => setPage(page + 1)} disabled={page === totalPages}>Next</button>
      </div>
      {/* Product Modal with Multiple Images */}
      {selectedProduct && (
        <div className="product-modal-backdrop" onClick={() => setSelectedProduct(null)}>
          <div className="product-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setSelectedProduct(null)}>&times;</button>
            <div className="modal-image-gallery">
              <img
                src={selectedProduct.images && selectedProduct.images.length > 0 ? selectedProduct.images[modalImageIdx] : 'https://via.placeholder.com/400x400?text=No+Image'}
                alt={selectedProduct.name}
                className="modal-product-image"
              />
              {selectedProduct.images && selectedProduct.images.length > 1 && (
                <div className="modal-thumbnails">
                  {selectedProduct.images.map((img, idx) => (
                    <img
                      key={img}
                      src={img}
                      alt={selectedProduct.name + ' thumbnail ' + (idx+1)}
                      className={"modal-thumbnail" + (idx === modalImageIdx ? " selected" : "")}
                      onClick={() => setModalImageIdx(idx)}
                    />
                  ))}
                </div>
              )}
            </div>
            <h2 className="modal-product-title">{selectedProduct.name}</h2>
            <p className="modal-product-description">{selectedProduct.description}</p>
            <div className="modal-product-price">{selectedProduct.prices[0] ? `$${(selectedProduct.prices[0].unit_amount / 100).toFixed(2)}` : 'N/A'}</div>
            <button className="add-to-cart-btn" onClick={() => { addToCart(selectedProduct.id, 1); setSelectedProduct(null); }} disabled={!selectedProduct.prices[0]}>Add to Cart</button>
          </div>
        </div>
      )}
    </div>
  );
}
